import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, Loader2Icon, ClockIcon, LightbulbIcon, UploadIcon, FileTextIcon, SparklesIcon, AlertCircleIcon } from 'lucide-react';
interface ArchitectLoadingProps {
  isOpen: boolean;
  onComplete: () => void;
  onError?: (message: string) => void;
  hasSource: boolean;
  sourceCount?: number;
}
interface Stage {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  duration: number;
}
export function ArchitectLoading({
  isOpen,
  onComplete,
  onError,
  hasSource,
  sourceCount = 0
}: ArchitectLoadingProps) {
  const [activeStage, setActiveStage] = useState(0);
  const [completedStages, setCompletedStages] = useState<Set<number>>(new Set());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stageTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Build stages conditionally based on whether sources exist
  const stages = useMemo<Stage[]>(() => {
    const result: Stage[] = [];
    if (hasSource) {
      result.push({
        id: 'upload',
        title: 'Uploading Files',
        subtitle: sourceCount > 1 ? `Uploading ${sourceCount} sources...` : 'Uploading your source...',
        icon: UploadIcon,
        duration: 2500
      });
      result.push({
        id: 'ingest',
        title: 'Ingesting Sources',
        subtitle: sourceCount > 1 ? `Parsing and analyzing ${sourceCount} documents...` : 'Parsing and analyzing your document...',
        icon: FileTextIcon,
        duration: 3000
      });
    }
    result.push({
      id: 'finalize',
      title: 'Finalizing Your Planner',
      subtitle: 'Polishing the blueprint for review',
      icon: SparklesIcon,
      duration: 2500
    });
    return result;
  }, [hasSource, sourceCount]);
  useEffect(() => {
    if (!isOpen) {
      setActiveStage(0);
      setCompletedStages(new Set());
      setElapsedSeconds(0);
      if (timerRef.current) clearInterval(timerRef.current);
      stageTimersRef.current.forEach(clearTimeout);
      stageTimersRef.current = [];
      return;
    }
    // Count-up timer
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    // Stage progression
    let cumulativeTime = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    stages.forEach((stage, idx) => {
      const activateTimer = setTimeout(() => {
        setActiveStage(idx);
      }, cumulativeTime);
      timers.push(activateTimer);
      cumulativeTime += stage.duration;
      const completeTimer = setTimeout(() => {
        setCompletedStages(prev => new Set([...prev, idx]));
      }, cumulativeTime);
      timers.push(completeTimer);
    });
    const completeTimer = setTimeout(() => {
      onComplete();
    }, cumulativeTime + 600);
    timers.push(completeTimer);
    // Safety timeout — if still open after 2x expected duration, signal error
    const totalExpected = cumulativeTime + 600;
    const safetyTimer = setTimeout(() => {
      if (onError) {
        onError('The planner is taking longer than expected. Please try again.');
      }
    }, totalExpected * 2 + 10000);
    timers.push(safetyTimer);
    stageTimersRef.current = timers;
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timers.forEach(clearTimeout);
    };
  }, [isOpen, onComplete, onError, stages]);
  // Expose a way for parent to trigger error externally — handled via onError prop
  // In production, parent would call setPhase('error') directly
  if (!isOpen) return null;
  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return <div className="w-full h-full flex items-center justify-center bg-white overflow-hidden" data-id="element-102">
      <div className="flex flex-col w-full max-w-md px-6 py-8" data-id="element-103">
        {/* Timer Row */}
        <div className="flex items-center justify-between mb-8" data-id="element-104">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-105">
            Building Your Planner
          </h2>
          <div className="flex items-center gap-2 text-gray-400 bg-gray-50 px-3 py-2 rounded-lg flex-shrink-0" data-id="element-106">
            <ClockIcon size={14} data-id="element-107" />
            <span className="text-sm font-mono font-medium text-gray-500" data-id="element-108">
              {formatTimer(elapsedSeconds)}
            </span>
          </div>
        </div>

        {/* Step Tracker */}
        <div className="space-y-0 mb-8" data-id="element-109">
          {stages.map((stage, idx) => {
          const isCompleted = completedStages.has(idx);
          const isActive = activeStage === idx && !isCompleted;
          const StageIcon = stage.icon;
          return <div key={stage.id} className="flex gap-4" data-id="element-110">
                {/* Vertical Line + Circle */}
                <div className="flex flex-col items-center" data-id="element-111">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isCompleted ? 'bg-[#00B75F] shadow-sm shadow-[#00B75F]/20' : isActive ? 'bg-white border-2 border-[#00B75F] shadow-sm shadow-[#00B75F]/10' : 'bg-white border-2 border-gray-200'}`} data-id="element-112">
                    {isCompleted ? <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} data-id="element-113">
                        <CheckIcon size={16} className="text-white" data-id="element-114" />
                      </motion.div> : isActive ? <Loader2Icon size={16} className="animate-spin text-[#00B75F]" data-id="element-115" /> : <StageIcon size={14} className="text-gray-300" data-id="element-116" />}
                  </div>
                  {idx < stages.length - 1 && <div className="w-0.5 flex-1 min-h-[24px] my-1 transition-colors duration-500" data-id="element-117">
                      <div className={`w-full h-full rounded-full transition-colors duration-500 ${isCompleted ? 'bg-[#00B75F]' : 'bg-gray-200'}`} data-id="element-118" />
                    </div>}
                </div>

                {/* Text */}
                <div className={`pb-6 ${idx === stages.length - 1 ? 'pb-0' : ''}`} data-id="element-119">
                  <p className={`text-base font-bold transition-colors duration-300 ${isCompleted || isActive ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-120">
                    {stage.title}
                    {isCompleted && <motion.span initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="ml-2 text-xs font-normal text-[#00B75F]" data-id="element-121">
                        Done
                      </motion.span>}
                  </p>
                  <AnimatePresence data-id="element-122">
                    {(isActive || isCompleted) && <motion.p initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} className={`text-xs mt-1 ${isActive ? 'text-gray-500 font-medium' : 'text-gray-400'}`} data-id="element-123">
                        {stage.subtitle}
                      </motion.p>}
                  </AnimatePresence>
                </div>
              </div>;
        })}
        </div>

        {/* Why a Planner? */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.5,
        duration: 0.4
      }} className="flex items-center gap-2.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100" data-id="element-124">
          <LightbulbIcon size={14} className="text-[#00B75F] flex-shrink-0" data-id="element-125" />
          <span data-id="element-126">
            The planner lets you review and adjust topics before generating
            questions.
          </span>
        </motion.div>
      </div>
    </div>;
}